import { DestroyRef, Directive, ElementRef, inject } from '@angular/core';
import { uiPx } from 'ra-ui';
import { floorTop, visibleArea } from './rail-menu.directive';

/**
 * Rail flutuante do card (a coluna de ações à direita): alinhado ao topo do card por
 * padrão, mas num card baixo perto do pé do painel ele descia para trás da barra fixa da seleção em lote
 * e as últimas ações sumiam. Quando o card é "entrado" (mouse ou foco), mede o rail e sobe
 * o que falta para ele caber inteiro acima dessa barra e do pé da área visível, sem passar
 * do topo visível. O deslocamento vai na var `--rail-lift` (o SCSS soma ao `top`).
 *
 * É a mesma conta do RaRailMenuDirective, que faz isso com os menus que abrem do rail.
 *
 * Depois de um clique no rail ele fica PRESO na tela até o mouse sair dele (ver `_pin`).
 */
@Directive({ selector: '[raRailFit]' })
export class RaRailFitDirective {
    private _el = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

    constructor() {
        const rail = this._el;
        const card = (): HTMLElement | null => rail.parentElement;
        const onEnter = (): void => this.fit();
        const onPress = (): void => this._pin();
        const onLeave = (): void => this._unpin();

        // O card nasce depois da diretiva no ciclo do template: espera o próximo frame.
        const frame = requestAnimationFrame(() => {
            card()?.addEventListener('mouseenter', onEnter);
            card()?.addEventListener('focusin', onEnter);
        });
        rail.addEventListener('pointerdown', onPress);
        rail.addEventListener('mouseleave', onLeave);

        inject(DestroyRef).onDestroy(() => {
            cancelAnimationFrame(frame);
            this._unpin();
            card()?.removeEventListener('mouseenter', onEnter);
            card()?.removeEventListener('focusin', onEnter);
            rail.removeEventListener('pointerdown', onPress);
            rail.removeEventListener('mouseleave', onLeave);
        });
    }

    /** Subida aplicada agora ("0px" = nenhuma); só reescreve quando muda. */
    private _lift = '0px';

    /** Topo do rail na tela enquanto ele está preso (null = solto). */
    private _pinnedTop: number | null = null;
    private _holdFrame = 0;

    /**
     * Recalcula a subida do rail para a posição atual do card. A conta parte da posição
     * NATURAL (topo do card + o `top` de 0.25rem do SCSS) e da altura do rail, nunca do
     * retângulo atual dele: medir o rail depois de zerar a subida pegava a posição no meio
     * da transição, e ele descia e subia a cada entrada no card ("sambava").
     */
    public fit(): void {
        // Preso, quem manda na posição é o `_hold`; o focusin do próprio clique cai aqui.
        if (this._pinnedTop !== null) return;

        const rail = this._el;
        const card = rail.parentElement;
        const height = rail.offsetHeight;
        if (!card || height === 0) return;

        const margin = uiPx(8);
        const railRect = rail.getBoundingClientRect();
        const top = card.getBoundingClientRect().top + uiPx(4);

        const area = visibleArea(rail);
        const bottom = Math.min(area.bottom, floorTop(railRect.left, railRect.right)) - margin;
        const overflow = top + height - bottom;
        const maxLift = top - (area.top + margin);
        const lift = overflow > 0 ? Math.max(0, Math.min(overflow, maxLift)) : 0;

        this._setOffset(-lift);
    }

    /**
     * Um clique no rail costuma mexer no card: a foto muda de tamanho, a prévia repagina e
     * o traço de "Página N" entra ou sai acima dele, a rolagem encosta no fim da lista. O
     * card anda e, com ele, o rail, e o clique seguinte caía no botão vizinho. Então, a
     * partir do clique, o rail fica onde está na tela (compensando a cada frame o quanto o
     * card andou) até o mouse sair dele ou o usuário rolar.
     */
    private _pin(): void {
        if (this._pinnedTop !== null) return;
        this._pinnedTop = this._el.getBoundingClientRect().top;
        this._holdFrame = requestAnimationFrame(this._hold);
        // Rolagem do usuário solta o rail: aí ele tem de ir junto com o card.
        window.addEventListener('wheel', this._onUserScroll, { capture: true, passive: true });
        window.addEventListener('touchmove', this._onUserScroll, { capture: true, passive: true });
    }

    private _onUserScroll = (): void => this._unpin();

    private _hold = (): void => {
        this._holdFrame = 0;
        const card = this._el.parentElement;
        if (this._pinnedTop === null || !card) return;

        this._setOffset(this._pinnedTop - (card.getBoundingClientRect().top + uiPx(4)));
        this._holdFrame = requestAnimationFrame(this._hold);
    };

    /** Solta o rail e o devolve ao lugar natural, já com o mouse fora dele. */
    private _unpin(): void {
        if (this._pinnedTop === null) return;
        this._pinnedTop = null;
        cancelAnimationFrame(this._holdFrame);
        this._holdFrame = 0;
        window.removeEventListener('wheel', this._onUserScroll, { capture: true });
        window.removeEventListener('touchmove', this._onUserScroll, { capture: true });
        this.fit();
    }

    /** Deslocamento do rail em relação ao topo natural (negativo = para cima). */
    private _setOffset(offset: number): void {
        const px = Math.round(offset);
        const value = px !== 0 ? `${px}px` : '0px';
        if (value === this._lift) return;
        this._lift = value;
        this._el.style.setProperty('--rail-lift', value);
    }
}
